import React, { useState } from 'react';

import { Button, Icon, Header } from 'semantic-ui-react';

import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import { useQuestionsContext } from 'components/modules/questions/context';
import { useQuestionFilters } from 'components/modules/questions/hooks/useQuestionFilters';
import { useQuestionModal } from 'components/modules/questions/hooks/useQuestionModal';
import {
  getSelectedQuestions,
  calculateBulkOperationResult,
} from 'components/modules/questions/utils';

import BulkOperationsBar from './components/BulkOperationsBar';
import QuestionCreateEditModal from './components/QuestionCreateEditModal';
import QuestionsFilters from './components/QuestionsFilters';
import QuestionsTable from './components/QuestionsTable';
import {
  BULK_OPERATION_ACTIVATE,
  BULK_OPERATION_ARCHIVE,
  BULK_OPERATION_ASSIGN_GROUPS,
  BULK_OPERATION_DELETE,
  QUESTION_STATUS_ACTIVE,
  QUESTION_STATUS_ARCHIVED,
} from '../../constants';

import './QuestionsTab.scss';

const propTypes = {};
const defaultProps = {};

export default function QuestionsTab() {
  const {
    questions,
    groups,
    deleteQuestion,
    bulkDeleteQuestions,
    bulkUpdateStatus,
    bulkAssignGroups,
    showMessage,
    refreshQuestions,
    refreshGroups,
  } = useQuestionsContext();

  const questionModal = useQuestionModal();
  const { filters, updateFilters, clearFilters } = useQuestionFilters();
  const { selectedGroup, searchQuery, selectedStatus } = filters;

  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);

  useAsyncEffect(async () => {
    await refreshQuestions(filters);
  }, [selectedGroup, searchQuery, selectedStatus]);

  function onQuestionSelect(questionId, checked) {
    if (checked) {
      setSelectedQuestionIds((prev) => [...prev, questionId]);
    } else {
      setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId));
    }
  }

  async function onQuestionSave(questionData) {
    await questionModal.saveQuestion(questionData);
    showMessage('Question saved successfully', 'success');
    await refreshGroups();
  }

  function onSelectAll(checked) {
    if (checked) {
      setSelectedQuestionIds(questions.map((q) => q.id));
    } else {
      setSelectedQuestionIds([]);
    }
  }

  function onBulkOperationSelect(operation, value) {
    const selectedQuestions = getSelectedQuestions(questions, selectedQuestionIds);
    const result = calculateBulkOperationResult(selectedQuestions, operation, value);

    if (result.success) {
      setSelectedQuestionIds([]);
      showMessage(result.message, 'success');

      switch (operation) {
        case BULK_OPERATION_DELETE:
          bulkDeleteQuestions(selectedQuestionIds);
          break;
        case BULK_OPERATION_ACTIVATE:
          bulkUpdateStatus(selectedQuestionIds, QUESTION_STATUS_ACTIVE);
          break;
        case BULK_OPERATION_ARCHIVE:
          bulkUpdateStatus(selectedQuestionIds, QUESTION_STATUS_ARCHIVED);
          break;
        case BULK_OPERATION_ASSIGN_GROUPS:
          if (value && value.length > 0) {
            bulkAssignGroups(selectedQuestionIds, value);
          }
          break;
        default:
          showMessage('Unknown operation', 'error');
      }
    } else {
      showMessage(result.message, 'error');
    }
  }

  const showNoResults = questions.length === 0 && questions.length > 0;

  return (
    <div className="questions-tab">
      <QuestionsFilters
        groups={groups}
        onFiltersChange={updateFilters}
        onCreateQuestion={questionModal.openCreateModal}
      />

      <BulkOperationsBar
        selectedCount={selectedQuestionIds.length}
        onBulkOperation={onBulkOperationSelect}
        groups={groups}
      />

      {showNoResults ? (
        <div className="empty-state">
          <Icon name="search" size="huge" />
          <Header as="h3">No questions found</Header>
          <p>Try adjusting your search criteria or filters</p>
          <Button className="action-button-gray" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      ) : (
        <QuestionsTable
          questions={questions}
          selectedQuestionIds={selectedQuestionIds}
          onQuestionSelect={onQuestionSelect}
          onSelectAll={onSelectAll}
          onEditQuestion={questionModal.openEditModal}
          onDeleteQuestion={deleteQuestion}
        />
      )}

      <QuestionCreateEditModal
        open={questionModal.showModal}
        question={questionModal.editingQuestion}
        groups={groups}
        onClose={questionModal.closeModal}
        onSave={onQuestionSave}
      />
    </div>
  );
}

QuestionsTab.propTypes = propTypes;
QuestionsTab.defaultProps = defaultProps;
